import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Components
import Navbar from './components/Navbar';
import Home from './components/Home';
import Login from './components/Login';
import Register from './components/Register';
import StudioLanding from './components/StudioLanding';
import LandingV8 from './components/landing-v8/LandingV8';
import Dashboard from './components/DashboardSimple';
import CreateComic from './components/CreateComic';
import CreateComicStreamlined from './components/CreateComicStreamlined';
import CreateComicFlow from './components/CreateComicFlow';
import ComicBlueprint from './components/ComicBlueprint';
import EditComic from './components/EditComic';
import EditComicStreamlined from './components/EditComicStreamlined';
import EditSeries from './components/EditSeries';
import ComicView from './components/ComicView';
import ComicViewStreamlined from './components/ComicViewStreamlined';
import Profile from './components/Profile';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import PasswordRecovery from './components/PasswordRecovery';
import ComicaCreator from './components/ComicaPro';
import ComicaProV2 from './components/editor/ComicaProV2';
import ComicaProV3 from './components/editor/ComicaProV3';
import TipSuccess from './components/TipSuccess';
import TipCancel from './components/TipCancel';
import CreatorSettings from './components/CreatorSettings';
import Community from './components/Community';
import Store from './components/Store';
import Earnings from './components/Earnings';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import CreditsPage from './components/CreditsPage';
import CreditSuccess from './components/CreditSuccess';
import CreditCancel from './components/CreditCancel';
import PrivacyPolicy from './components/PrivacyPolicy';
import Footer from './components/Footer';
import Contact from './components/Contact';
import Notifications from './components/Notifications';
import { Toaster } from './components/ui/sonner';
import { identifyUser, resetTracking } from './utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Track whether a token refresh is already in progress (prevents concurrent refreshes)
  const isRefreshing = React.useRef(false);
  const failedQueue = React.useRef([]);

  const processQueue = (error, newToken = null) => {
    failedQueue.current.forEach(prom => {
      if (error) prom.reject(error);
      else prom.resolve(newToken);
    });
    failedQueue.current = [];
  };

  // Attempt to refresh the access token using stored refresh token
  const attemptRefresh = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;
    try {
      const res = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
      const { access_token, refresh_token: newRefresh } = res.data;
      localStorage.setItem('token', access_token);
      if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
      setToken(access_token);
      return access_token;
    } catch {
      return null;
    }
  };

  // Axios interceptor: auto-refresh on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        // Only intercept 401s (not login/register/refresh endpoints)
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/login') &&
          !originalRequest.url?.includes('/auth/register') &&
          !originalRequest.url?.includes('/auth/refresh')
        ) {
          if (isRefreshing.current) {
            // Another refresh is in progress — queue this request
            return new Promise((resolve, reject) => {
              failedQueue.current.push({ resolve, reject });
            }).then(newToken => {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(originalRequest);
            }).catch(err => Promise.reject(err));
          }

          originalRequest._retry = true;
          isRefreshing.current = true;

          const newToken = await attemptRefresh();
          isRefreshing.current = false;

          if (newToken) {
            processQueue(null, newToken);
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          } else {
            processQueue(error);
            // Refresh failed — session truly expired
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            setToken(null);
            setUser(null);
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/users/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          // Re-identify returning user in PostHog
          identifyUser(response.data.id, { username: response.data.username, display_name: response.data.display_name });
        } catch (error) {
          console.error('Token validation failed, attempting refresh...');
          // Try refreshing before giving up
          const newToken = await attemptRefresh();
          if (newToken) {
            try {
              const response = await axios.get(`${API}/users/me`, {
                headers: { Authorization: `Bearer ${newToken}` }
              });
              setUser(response.data);
            } catch {
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              setToken(null);
            }
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            setToken(null);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = (userData, accessToken, refreshToken) => {
    setUser(userData);
    setToken(accessToken);
    localStorage.setItem('token', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    resetTracking(); // Reset PostHog identity on logout
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50">
        <div className="text-xl font-medium text-gray-600">Loading Comica...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, token, login, logout, API }}>
      <Router>
        <AppContent user={user} />
      </Router>
    </AuthContext.Provider>
  );
}

// Separate component to use useLocation hook
function RequireAuth({ user, children }) {
  const location = useLocation();

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          returnTo: `${location.pathname}${location.search}`,
          returnState: location.state
        }}
      />
    );
  }

  return children;
}

function AppContent({ user }) {
  const location = useLocation();
  const path = location.pathname;
  const isAdminRoute = path.startsWith('/admin');
  const isStudioLanding = path === '/comica';
  const isMarketingLanding = path === '/';

  // Routes where Footer should NOT appear
  const hideFooterRoutes = [
    '/admin',
    '/comic/',
    '/comic/streamlined/',
    '/streamlined-comic/',
    '/create-comic',
    '/create-streamlined',
    '/edit-comic',
    '/edit-streamlined',
    '/comica-creator',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/reset-password',
    '/recover-password',
    '/create-comic-flow',
    '/create-comic-flow',
    '/comic-blueprint',
    '/v2',
    '/v3',
    '/comics'
  ];

  // Routes where Navbar should NOT appear
  const hideNavbarRoutes = [
    '/v2',
    '/v3',
    '/comic-blueprint' // Blueprint editor also usually needs full screen
  ];

  const showNavbar = !isAdminRoute && !isStudioLanding && !hideNavbarRoutes.some(route => path.startsWith(route));
  const showFooter = !isStudioLanding && !hideFooterRoutes.some(route => path.startsWith(route));

  return (
    <div className={`App min-h-screen ${isMarketingLanding ? 'bg-[#07060c]' : 'bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50'} flex flex-col`}>
      {showNavbar && <Navbar />}
      <main className={`flex-grow ${showNavbar ? "pt-16" : ""}`}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingV8 />} />
          <Route path="/comica" element={<StudioLanding />} />
          <Route path="/comics" element={<Home />} />
          <Route path="/login" element={!user ? <Login /> : <Navigate to={location.state?.returnTo || '/dashboard'} replace />} />
          <Route path="/register" element={!user ? <Register /> : <Navigate to={location.state?.returnTo || '/dashboard'} replace />} />
          <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
          <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/dashboard" />} />
          <Route path="/recover-password" element={<PasswordRecovery />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/contact" element={<Contact />} />

          <Route path="/dashboard" element={<RequireAuth user={user}><Dashboard /></RequireAuth>} />
          <Route path="/dashboard-simple" element={<RequireAuth user={user}><Dashboard /></RequireAuth>} />
          <Route path="/comica-creator" element={<RequireAuth user={user}><ComicaCreator /></RequireAuth>} />
          {/* V2 now points to V3 — latest editor. Old /v2 links still work. */}
          <Route path="/v2" element={<ComicaProV3 width={1024} height={768} />} />
          <Route path="/v3" element={<ComicaProV3 width={1024} height={768} />} />
          <Route path="/create-comic" element={<RequireAuth user={user}><CreateComicStreamlined /></RequireAuth>} />
          <Route path="/create-comic-streamlined" element={<RequireAuth user={user}><CreateComicStreamlined /></RequireAuth>} />
          <Route path="/create-comic-old" element={<RequireAuth user={user}><CreateComic /></RequireAuth>} />
          <Route path="/create-comic-flow" element={<RequireAuth user={user}><CreateComicFlow /></RequireAuth>} />
          <Route path="/comic-blueprint" element={<Navigate to="/comica" replace />} />
          <Route path="/edit-comic/:id" element={<RequireAuth user={user}><EditComicStreamlined /></RequireAuth>} />
          <Route path="/edit-comic-streamlined/:id" element={<RequireAuth user={user}><EditComicStreamlined /></RequireAuth>} />
          <Route path="/edit-comic-old/:id" element={<RequireAuth user={user}><EditComic /></RequireAuth>} />
          <Route path="/edit-series/:id" element={<RequireAuth user={user}><EditSeries /></RequireAuth>} />
          <Route path="/comic/:id" element={<ComicView />} />
          <Route path="/comic/streamlined/:id" element={<ComicViewStreamlined />} />
          <Route path="/streamlined-comic/:id" element={<ComicViewStreamlined />} />
          <Route path="/profile/:username" element={<Profile />} />
          <Route path="/tip-success" element={<TipSuccess />} />
          <Route path="/tip-cancel" element={<TipCancel />} />
          <Route path="/creator-settings" element={<RequireAuth user={user}><CreatorSettings /></RequireAuth>} />
          <Route path="/community" element={<Community />} />
          <Route path="/store" element={<Store />} />
          <Route path="/earnings" element={<RequireAuth user={user}><Earnings /></RequireAuth>} />
          <Route path="/notifications" element={<RequireAuth user={user}><Notifications /></RequireAuth>} />
          {/* Credit Purchase Routes */}
          <Route path="/credits" element={<RequireAuth user={user}><CreditsPage /></RequireAuth>} />
          <Route path="/credits/success" element={<RequireAuth user={user}><CreditSuccess /></RequireAuth>} />
          <Route path="/credits/cancel" element={<RequireAuth user={user}><CreditCancel /></RequireAuth>} />
          {/* Admin Routes - Invisible to regular users */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
        </Routes>
      </main>

      {/* Footer */}
      {showFooter && !isAdminRoute && <Footer />}
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

export default App;
